/* =====================================================================================
   Seed identifiers to database identifiers.

   THE BUG THIS EXISTS TO REMOVE.

   The Atlas renders from the bundled seed, where a centre is identified by a readable slug:
   'usa-nyu-langone'. In Postgres, atlas.sites.id is a uuid. Every write keyed to a centre
   therefore sent a slug into a uuid column, and Postgres rejected it:

       invalid input syntax for type uuid: "usa-nyu-langone"

   That was not one broken form. It was every site-scoped write in the application, because
   the same site.id flows into opportunities, interactions, technical records and field
   overrides. The reads were equally affected and failed more quietly, returning nothing
   rather than erroring, which is why it looked like missing data rather than a defect.

   WHY CONVERSION RATHER THAN A LOOKUP.

   The loader mints those uuids deterministically (research/build_atlas_sql.py: uuid5 over a
   fixed namespace), specifically so that re-running the seed updates rows instead of
   duplicating them. That property makes the slug sufficient to recompute the id, so no
   lookup table, no round trip and no possibility of the two drifting apart.

   The derivation below is checked against the Python loader in scripts/verify-site-ids.mjs,
   and that script also confirms every slug in the seed resolves to a row that actually
   exists. A hash will happily produce a well-formed uuid for a centre that was never loaded,
   so agreement on the algorithm is necessary but not sufficient.

   WHY IT LIVES AT THE DATA LAYER.

   Converting at each call site is the version of this fix that rots: the next form added
   passes site.id straight through and the bug returns silently. Every function in lib/ that
   accepts a site identifier converts it here instead, so a caller cannot get it wrong and
   does not need to know the distinction exists.
   ===================================================================================== */

import { v5 as uuidv5 } from 'uuid';

/* Fixed for the lifetime of the dataset. Changing it re-points every identifier in the
   Atlas at nothing, so it is duplicated in research/build_atlas_sql.py and must stay
   identical in both places. */
const ATLAS_NS = '6f1d5a2c-0b3e-4c7a-9d18-2a5b7c4e9f60';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (v: string): boolean => UUID_RE.test(v);

/**
 * The database id for an Atlas entity, from either a seed slug or a database id.
 *
 * Idempotent by design. Rows created through the application already carry a real uuid, and
 * rows rendered from the seed carry a slug; both reach the same functions and both must work.
 */
function dbId(kind: 'site' | 'country' | 'pathway', seedId: string | null | undefined): string | null {
  if (!seedId) return null;
  if (isUuid(seedId)) return seedId;                  // already a database id: pass through
  return uuidv5(`ariadne-forum:atlas:${kind}:${seedId}`, ATLAS_NS);
}

export const dbSiteId = (id: string | null | undefined) => dbId('site', id);
export const dbCountryId = (id: string | null | undefined) => dbId('country', id);
export const dbPathwayId = (id: string | null | undefined) => dbId('pathway', id);
